import { gidToId } from '@shared/shop-api/core/helper'
import {
  getMetafieldBoolean,
  getMetafieldDateTime,
  getMetafieldDimension,
  getMetafieldJson,
  getMetafieldList,
  getMetafieldString,
  getMetafieldVolume,
  getMetafieldWeight,
} from '@shared/shop-api/metafield/mapper'
import { isPromotedProductRulePercentageFragment } from '@shared/shop-api/product/attributes/check'
import type {
  BatteryUnNumber,
  PromotedProductFragment,
  PromotedProductRuleAmountFragment,
  PromotedProductRulePercentageFragment,
  PromotedProductsFragment,
} from '@shared/shop-api/product/attributes/types'
import type { MetafieldFragment } from '@shared/types/storefront/graphql'

export const toProductAttributes = (
  metafields: (MetafieldFragment | null)[]
) => {
  const edition = getMetafieldList(metafields, 'edition')

  return {
    brand: import.meta.env.PUBLIC_BRAND,
    batteryUnNumber: getMetafieldString<BatteryUnNumber>(
      metafields,
      'battery_un_number'
    ),
    edition,
    collection: getMetafieldList(metafields, 'collection'),
    colors: getMetafieldList(metafields, 'colors'),
    depth: getMetafieldDimension(metafields, 'depth'),
    featureDescription: getMetafieldString(metafields, 'feature_description'),
    shortDescription: getMetafieldString(metafields, 'description_short'),
    materialDescription: getMetafieldString(metafields, 'material_description'),
    instructionsForUse: getMetafieldString(metafields, 'instructions_for_use'),
    height: getMetafieldDimension(metafields, 'height'),
    width: getMetafieldDimension(metafields, 'width'),
    launchDate: getMetafieldDateTime(metafields, 'launch_date'),
    model: getMetafieldString(metafields, 'model'),
    name: getMetafieldString(metafields, 'name'),
    restockDate: getMetafieldDateTime(metafields, 'restock_date'),
    volume: getMetafieldVolume(metafields, 'volume'),
    responsibility: getMetafieldString(metafields, 'responsibility'),
    season: getMetafieldString(metafields, 'season'),
    style: getMetafieldString(metafields, 'style'),
    styleSwitcherColorCode: getMetafieldString(
      metafields,
      'style_switcher_color_code'
    ),
    subCategory: getMetafieldString(metafields, 'sub_category'),
    type: getMetafieldString(metafields, 'type'),
    weight: getMetafieldWeight(metafields, 'weight'),
    caseableSku: getMetafieldString(metafields, 'caseable_sku'),
    features: getMetafieldList(metafields, 'features'),
    productContent: getMetafieldString(
      metafields,
      'product_content_collection'
    ),
    materialType: getMetafieldString(metafields, 'material_type'),
    isNew: getMetafieldBoolean(metafields, 'new'),
    careInstructions: getMetafieldString(metafields, 'care_instructions'),
    deliveryTime: getMetafieldString(metafields, 'delivery_time'),
    themeWorld: getMetafieldString(metafields, 'theme_world'),
    promotedProducts: getMetafieldJson<PromotedProductsFragment>(
      metafields,
      'promoted_products',
      []
    ).map(toPromotedProduct),
    modelUntranslated: getMetafieldString(metafields, 'model_untranslated'),
    styleUntranslated: getMetafieldString(metafields, 'style_untranslated'),
    nameUntranslated: getMetafieldString(metafields, 'name_untranslated'),

    /**
     * @deprecated use `materialType` instead
     */
    outerMaterialType: getMetafieldString(metafields, 'material_type'),
    /**
     * @deprecated use `styleSwitcherColorCode` instead
     */
    colorCode: getMetafieldString(metafields, 'style_switcher_color_code'),
    /**
     * @deprecated use `edition` instead
     */
    editions: edition ? [edition] : [],
    /**
     * @deprecated use `productContent` instead
     */
    additionalContent: getMetafieldString(
      metafields,
      'product_content_collection'
    ),
  }
}

export const toPromotedProduct = (
  promotedProducts: PromotedProductFragment
) => {
  return {
    promotionTitle: promotedProducts.promotionTitle,
    discountId: promotedProducts.discountId,
    startsAt: promotedProducts.startsAt
      ? new Date(promotedProducts.startsAt)
      : undefined,
    endsAt: promotedProducts.endsAt
      ? new Date(promotedProducts.endsAt)
      : undefined,
    discountRule: {
      quantity: parseInt(promotedProducts.discountRule.quantity),
      type: isPromotedProductRulePercentageFragment(
        promotedProducts.discountRule.type
      )
        ? toPromotedProductRulePercentage(promotedProducts.discountRule.type)
        : promotedProducts.discountRule.type,
    },
    items: promotedProducts.items.map(gidToId),
  }
}

export const toPromotedProductRulePercentage = (
  percentageRule: PromotedProductRulePercentageFragment
) => {
  return {
    percentage: parseFloat(percentageRule.percentage),
  }
}

export const toPromotedProductRuleAmount = (
  amountRule: PromotedProductRuleAmountFragment
) => {
  return {
    amount: parseFloat(amountRule.amount),
    currencyCode: amountRule.currencyCode,
  }
}
